/**
 * GlobalPatientSearch — ⌘K / Ctrl+K palette that lets front-desk staff
 * search across Patients, Front-Desk Tasks, Appointments, and Inbox
 * Conversations. Hits /api/search/global (Mongo-first, ~50ms typical).
 * Opens the PatientLookupSlider or navigates to the relevant module.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Loader2, HeartPulse, MessageCircle,
  Cake, Phone, Mail, Clock, X, User as UserIcon,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useAuth } from '../../contexts/AuthContext';
import { usePatientLookup } from '../../contexts/PatientLookupContext';
import api from '../../lib/api';

// ── Recent patients (localStorage) — keeps last 5, deduped by contactId ──
const RECENT_KEY = 'recent_patients_v1';
const RECENT_MAX = 5;

function readRecents() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, RECENT_MAX) : [];
  } catch { return []; }
}

function pushRecent(p) {
  if (!p?.contactId) return;
  try {
    const current = readRecents();
    const filtered = current.filter(x => x.contactId !== p.contactId);
    const entry = {
      contactId: p.contactId,
      name: p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim(),
      email: p.email || '',
      phone: p.phone || '',
      dateOfBirth: p.dateOfBirth || '',
      juvonno_id: p.juvonno_id || '',
      ts: Date.now(),
    };
    const next = [entry, ...filtered].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent('recent-patients:updated'));
  } catch { /* noop */ }
}

// ── Category presentation config ─────────────────────────────
const CATEGORIES = [
  { key: 'patients',      label: 'Patients',      icon: HeartPulse,     accent: 'text-indigo-600', bg: 'bg-indigo-50', activeBg: 'bg-indigo-50',  activeText: 'text-indigo-700', activeAvatar: 'bg-indigo-600' },
  { key: 'conversations', label: 'Conversations', icon: MessageCircle,  accent: 'text-sky-600',    bg: 'bg-sky-50',    activeBg: 'bg-sky-50',     activeText: 'text-sky-700',    activeAvatar: 'bg-sky-600'    },
];

const CATEGORY_BY_KEY = Object.fromEntries(CATEGORIES.map(c => [c.key, c]));

// Flatten the categorized results into one linear list for keyboard nav.
// Preserves category order.
function flattenResults(bucketed) {
  const flat = [];
  CATEGORIES.forEach(cat => {
    (bucketed[cat.key] || []).forEach(item => flat.push({ ...item, __cat: cat.key }));
  });
  return flat;
}

// Format a compact date like "Jul 9 · 5:30 PM" from ISO string
function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d)) return String(iso).slice(0, 16);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return String(iso).slice(0, 16); }
}

export function GlobalPatientSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [bucketed, setBucketed] = useState({ patients: [], conversations: [] });
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [recents, setRecents] = useState(() => readRecents());
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { currentLocation } = useAuth();
  const { openLookup } = usePatientLookup();
  const locationId = currentLocation?.id;

  // Global ⌘K / Ctrl+K shortcut + custom event trigger for header/toolbar buttons
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const onOpenEvent = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('global-patient-search:open', onOpenEvent);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('global-patient-search:open', onOpenEvent);
    };
  }, []);

  // Focus input when opening; reset state when closing
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 60);
      setHighlight(0);
      setRecents(readRecents());
    } else {
      setQ('');
      setBucketed({ patients: [], conversations: [] });
    }
  }, [open]);

  // Keep recents in sync if opened from another tab or updated elsewhere
  useEffect(() => {
    const onUpdate = () => setRecents(readRecents());
    window.addEventListener('recent-patients:updated', onUpdate);
    window.addEventListener('storage', onUpdate);
    return () => {
      window.removeEventListener('recent-patients:updated', onUpdate);
      window.removeEventListener('storage', onUpdate);
    };
  }, []);

  // Debounced multi-module search
  useEffect(() => {
    if (!open || !locationId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) {
      setBucketed({ patients: [], conversations: [] });
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get('/search/global', {
          params: { location_id: locationId, q: q.trim(), limit: 5, include: 'patients,conversations' },
        });
        const d = r.data || {};
        setBucketed({
          patients: d.patients || [],
          conversations: d.conversations || [],
        });
      } catch {
        setBucketed({ patients: [], conversations: [] });
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, locationId, open]);

  const selectResult = useCallback((item) => {
    if (!item) return;
    setOpen(false);

    const kind = item.type || item.__cat?.replace(/s$/, '');
    if (kind === 'patient' || item.type === 'patient') {
      if (!item.contactId) return;
      pushRecent(item);
      setRecents(readRecents());
      openLookup(item.contactId, item.name);
      return;
    }
    if (kind === 'task' || item.type === 'task') {
      // Deep-link into the tasks page — the page pulses/scrolls to the row.
      if (item.id) {
        navigate(`/front-desk-tasks?taskId=${encodeURIComponent(item.id)}`);
      } else {
        navigate('/front-desk-tasks');
      }
      return;
    }
    if (kind === 'conversation' || item.type === 'conversation') {
      // Deep-link into the inbox — InboxPage watches ?contact=<id> and auto-selects
      // the matching conversation once its list loads.
      if (item.contactId) {
        navigate(`/inbox?contact=${encodeURIComponent(item.contactId)}`);
      } else {
        navigate('/inbox');
      }
      return;
    }
    if (kind === 'appointment' || item.type === 'appointment') {
      // Deep-link into scheduling — jump to the appt's date and open the
      // AppointmentDetailDialog directly. contactId is included so the dialog
      // can hydrate itself even if the appt lives on a hidden PT calendar.
      const day = (item.startTime || '').slice(0, 10);
      const parts = [];
      if (day && /^\d{4}-\d{2}-\d{2}$/.test(day)) parts.push(`date=${day}`);
      if (item.id) parts.push(`appt=${encodeURIComponent(item.id)}`);
      if (item.contactId) parts.push(`apptContact=${encodeURIComponent(item.contactId)}`);
      const qs = parts.length ? `?${parts.join('&')}` : '';
      navigate(`/scheduling${qs}`);
    }
  }, [navigate, openLookup]);

  const clearRecents = useCallback(() => {
    try { localStorage.removeItem(RECENT_KEY); } catch { /* noop */ }
    setRecents([]);
    window.dispatchEvent(new CustomEvent('recent-patients:updated'));
  }, []);

  // Flat list for keyboard navigation
  const flatResults = useMemo(() => flattenResults(bucketed), [bucketed]);
  const activeList = q.trim().length < 2 ? recents : flatResults;

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, activeList.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && activeList[highlight]) { e.preventDefault(); selectResult(activeList[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  const totalResults = flatResults.length;
  const showEmpty = q.trim().length >= 2 && totalResults === 0 && !loading;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        // [&>button.absolute]:hidden hides the built-in shadcn X close button
        // (we have ESC key + ESC kbd hint, no need for the redundant X).
        className="max-w-lg w-[92vw] p-0 gap-0 top-[15%] translate-y-0 [&>button.absolute]:hidden"
        data-testid="global-patient-search"
      >
        <VisuallyHidden>
          <DialogTitle>Global Search</DialogTitle>
          <DialogDescription>Search patients, tasks, appointments, and inbox conversations. Press Enter to open.</DialogDescription>
        </VisuallyHidden>

        <div className="flex items-center gap-2.5 border-b border-slate-200 px-4 py-3">
          <Search size={16} className="text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search patients or conversations…"
            className="flex-1 bg-transparent outline-none text-[13.5px] placeholder:text-slate-400"
            data-testid="global-search-input"
          />
          {loading && <Loader2 size={14} className="animate-spin text-slate-400 shrink-0" />}
          <kbd className="hidden sm:inline-block text-[9.5px] font-semibold text-slate-400 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded" data-testid="esc-hint">
            ESC
          </kbd>
        </div>

        <div className="max-h-[460px] overflow-y-auto">
          {q.trim().length < 2 ? (
            recents.length > 0 ? (
              <RecentSection
                recents={recents}
                highlight={highlight}
                onHover={setHighlight}
                onSelect={selectResult}
                onClear={clearRecents}
              />
            ) : (
              <div className="py-10 text-center">
                <div className="text-[11px] text-slate-500 mb-1">Type at least 2 characters to search</div>
                <div className="text-[10px] text-slate-400">Searches patients &amp; inbox conversations · ↑↓ to navigate · Enter to open</div>
              </div>
            )
          ) : showEmpty ? (
            <div className="py-10 text-center text-[12px] text-slate-500">
              No results for &ldquo;{q}&rdquo;
            </div>
          ) : (
            <div data-testid="global-search-results">
              {CATEGORIES.map(cat => {
                const items = bucketed[cat.key] || [];
                if (items.length === 0) return null;
                const catStartIndex = flatResults.findIndex(r => r.__cat === cat.key);
                return (
                  <div key={cat.key} className="border-b border-slate-100 last:border-b-0" data-testid={`category-${cat.key}`}>
                    <div className="flex items-center gap-1.5 px-4 pt-3 pb-1.5">
                      <cat.icon size={11} strokeWidth={2.25} className={cat.accent} />
                      <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                        {cat.label}
                      </span>
                      <span className="text-[9.5px] font-mono text-slate-400 bg-slate-100 border border-slate-200 px-1.5 py-px rounded">
                        {items.length}
                      </span>
                    </div>
                    <ul className="pb-1">
                      {items.map((item, i) => {
                        const idx = catStartIndex + i;
                        const active = idx === highlight;
                        return (
                          <li key={`${cat.key}-${item.id}-${i}`}>
                            <ResultRow
                              cat={cat}
                              item={item}
                              active={active}
                              onHover={() => setHighlight(idx)}
                              onSelect={() => selectResult(item)}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Recent patients section (unchanged behaviour) ────────────
function RecentSection({ recents, highlight, onHover, onSelect, onClear }) {
  return (
    <div>
      <div className="flex items-center justify-between px-4 pt-3 pb-1.5">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
          <Clock size={11} strokeWidth={2.25} className="text-teal-600" />
          Recent
        </div>
        <button
          type="button"
          onClick={onClear}
          className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-400 hover:text-rose-600 transition-colors"
          data-testid="clear-recents-btn"
        >
          <X size={10} /> Clear
        </button>
      </div>
      <ul className="py-1" data-testid="global-search-recents">
        {recents.map((p, i) => {
          const active = i === highlight;
          const initials = (p.name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
          return (
            <li key={p.contactId}>
              <button
                type="button"
                onClick={() => onSelect({ ...p, type: 'patient' })}
                onMouseEnter={() => onHover(i)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                  active ? 'bg-teal-50' : 'hover:bg-slate-50'
                }`}
                data-testid={`global-search-recent-${p.contactId}`}
              >
                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${
                  active ? 'bg-teal-600 text-white' : 'bg-slate-200 text-slate-700'
                }`}>
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold text-slate-900 truncate">
                      {p.name || 'Unnamed'}
                    </span>
                    {p.juvonno_id && (
                      <span className="text-[9.5px] font-mono text-teal-700 bg-teal-50 border border-teal-200 px-1 py-px rounded">
                        #{p.juvonno_id}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-0.5">
                    {p.email && (<span className="flex items-center gap-1 truncate max-w-[180px]"><Mail size={9} /> {p.email}</span>)}
                    {p.phone && (<span className="flex items-center gap-1"><Phone size={9} /> {p.phone}</span>)}
                    {p.dateOfBirth && (<span className="flex items-center gap-1"><Cake size={9} /> {p.dateOfBirth}</span>)}
                  </div>
                </div>
                <UserIcon size={12} className={active ? 'text-teal-600' : 'text-slate-300'} />
              </button>
            </li>
          );
        })}
      </ul>
      <div className="px-4 py-2 border-t border-slate-100 text-[10px] text-slate-400 text-center">
        Type at least 2 characters to search everywhere
      </div>
    </div>
  );
}

// ── Result row: one line, category-tinted on active/hover ──────
function ResultRow({ cat, item, active, onHover, onSelect }) {
  const Icon = cat.icon;

  // Per-type row content
  let primary = '';
  let secondary = null;
  let subtle = null;
  let testid = `global-search-result-${cat.key}-${item.id}`;

  if (cat.key === 'patients') {
    primary = item.name || 'Unnamed';
    secondary = (
      <>
        {item.email && (<span className="flex items-center gap-1 truncate max-w-[180px]"><Mail size={9} /> {item.email}</span>)}
        {item.phone && (<span className="flex items-center gap-1"><Phone size={9} /> {item.phone}</span>)}
        {item.status && (<span className="text-[10px] font-medium text-slate-500 uppercase">{item.status}</span>)}
      </>
    );
  } else if (cat.key === 'conversations') {
    primary = item.contactName || 'Conversation';
    subtle = item.lastMessage ? item.lastMessage.slice(0, 120) : null;
    secondary = (
      <>
        {item.lastMessageType && (<span className="text-[9.5px] font-bold uppercase text-slate-500">{item.lastMessageType}</span>)}
        {item.lastMessageDate && (<span className="flex items-center gap-1"><Clock size={9} /> {fmtDate(item.lastMessageDate)}</span>)}
        {item.unread > 0 && (
          <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold">
            {item.unread}
          </span>
        )}
      </>
    );
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      onMouseEnter={onHover}
      className={`w-full flex items-start gap-3 px-4 py-2.5 text-left transition-colors ${
        active ? cat.activeBg : 'hover:bg-slate-50'
      }`}
      data-testid={testid}
    >
      <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
        active ? cat.activeAvatar : cat.bg
      }`}>
        <Icon size={14} strokeWidth={2} className={active ? 'text-white' : cat.accent} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold text-slate-900 truncate leading-tight">
          {primary}
        </div>
        {subtle && (
          <div className="text-[11.5px] text-slate-600 truncate mt-0.5">
            {subtle}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500 mt-0.5">
          {secondary}
        </div>
      </div>
    </button>
  );
}

export default GlobalPatientSearch;
